import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook for managing admin session timeout and activity tracking
 */
export const useAdminSession = (timeoutMinutes = 30) => {
  const { signOut, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(timeoutMinutes * 60);

  const TIMEOUT_MS = timeoutMinutes * 60 * 1000; // Convert to milliseconds
  const WARNING_MS = 5 * 60 * 1000; // Show warning 5 minutes before timeout

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    setSessionWarning(false);
  }, []);

  // Handle session timeout
  const handleTimeout = useCallback(async () => {
    try {
      await signOut();
      navigate('/sign-in?message=session_expired&redirect_url=' + encodeURIComponent('/admin'));
    } catch (error) {
      console.error('Error during session timeout:', error);
      // Force navigation even if signOut fails
      navigate('/sign-in?message=session_expired&redirect_url=' + encodeURIComponent('/admin'));
    }
  }, [signOut, navigate]);

  // Extend session (reset timeout)
  const extendSession = useCallback(() => {
    updateActivity();
    setSessionWarning(false);
  }, [updateActivity]);

  // Set up activity listeners and timeout checking
  useEffect(() => {
    if (!isSignedIn) return;

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Add event listeners for user activity
    const handleActivity = () => updateActivity();
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Check session timeout every minute
    const checkInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const remaining = Math.max(0, TIMEOUT_MS - timeSinceActivity);
      
      setTimeRemaining(Math.floor(remaining / 1000));

      if (timeSinceActivity >= TIMEOUT_MS) {
        // Session has expired
        handleTimeout();
      } else if (remaining <= WARNING_MS && !sessionWarning) {
        // Show warning
        setSessionWarning(true);
      }
    }, 60000); // Check every minute

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearInterval(checkInterval);
    };
  }, [isSignedIn, lastActivity, sessionWarning, handleTimeout, updateActivity, TIMEOUT_MS, WARNING_MS]);

  return {
    sessionWarning,
    timeRemaining,
    extendSession,
    handleTimeout,
    lastActivity: new Date(lastActivity)
  };
};

export default useAdminSession;
